import React, { useMemo } from "react";
import useMatchupStore from "../store/MatchupStore";
import {
  calculateStatDelta,
  renderStat,
  renderStatDelta,
} from "../utils/matchupUtils";
import { TableHeader } from "./TableHeader";
import { useViewport } from "../../components/hooks/useViewport";
import {
  getFixtureByTeamId,
  getOpponentFromFixture,
  getTeamFromFixture,
} from "../../../global/lib/utils/fixtures";
import { IconPlayingStatus } from "../../../global/components/icons/IconPlayingStatus";
import { LineupCaptainIcon } from "./LineupCaptainIcon";
import { formatDateInLocalTimezone } from "../../lib/utils/sportFixtures";

function PlayerStatsTable({ staticData }) {
  const { formula, lineupFormation } = staticData;
  const { statCols } = formula;

  const [team1, team2, playerStats, prevPlayerStats, selectedTeam] =
    useMatchupStore((state) => [
      state.team1,
      state.team2,
      state.playerStats,
      state.prevPlayerStats,
      state.selectedTeam,
    ]);

  const fixtureTeam1 = team1;
  const fixtureTeam2 = team2;
  const lineup1 = team1.lineup;
  const lineup2 = team2.lineup;

  // Map player stats by ID for quick lookup
  const playerStatsByPlayerId = useMemo(() => {
    const statsMap = {};
    if (playerStats && playerStats.length > 0) {
      playerStats.forEach((stats) => {
        statsMap[stats.player_id] = stats;
      });
    }
    return statsMap;
  }, [playerStats]);

  // Map previous player stats by ID for delta calculations
  const prevPlayerStatsByPlayerId = useMemo(() => {
    const statsMap = {};
    if (prevPlayerStats && prevPlayerStats.length > 0) {
      prevPlayerStats.forEach((stats) => {
        statsMap[stats.player_id] = stats;
      });
    }
    return statsMap;
  }, [prevPlayerStats]);

  function isLive(player) {
    if (!player) {
      return false;
    }

    const hasGameStats = playerStatsByPlayerId[player.id];

    if (!hasGameStats) {
      return false;
    }

    const [sportFixtures] = useMatchupStore((state) => [state.sportFixtures]);
    const playerFixture = getFixtureByTeamId(sportFixtures, player.teamId);

    if (!playerFixture) {
      return false;
    }

    return playerFixture.status === 1; // STATUS_INPROGRESS
  }

  function isScoring(fixtureTeam, player) {
    if (!player) {
      return false;
    }

    const [sportFixtures] = useMatchupStore((state) => [state.sportFixtures]);
    const playerFixture = getFixtureByTeamId(sportFixtures, player.teamId);

    if (!playerFixture) {
      return false;
    }

    if (playerFixture.status !== 0) {
      // STATUS_PREMATCH
      const hasGameStats = playerStatsByPlayerId[player.id];

      if (!hasGameStats) {
        return false;
      }
    }

    return fixtureTeam.stats.scoringPlayerIds.includes(player.id);
  }

  // Component for player row
  function PlayerRow({ lineup, fixtureTeam, player, statCols }) {
    const [leagueSeasonId] = useMatchupStore((state) => [state.leagueSeasonId]);

    const gamelog = player ? playerStatsByPlayerId[player.id] : null;
    const prevGamelog = player ? prevPlayerStatsByPlayerId[player.id] : null;
    const scoringCaptain =
      player && player.id === fixtureTeam.stats.scoringCaptain;

    const isPlayerLive = isLive(player);
    const isPlayerScoring = isScoring(fixtureTeam, player);
    const playingStatus = player?.playingStatus;

    const cellClasses = ` 
      ${!isPlayerScoring ? "opacity-50" : ""} 
      ${scoringCaptain ? "bg-purple-50 font-medium text-purple-700 dark:bg-gray-800 dark:text-purple-400" : "md:font-normal"}
    `;

    const bgClasses = isPlayerLive
      ? "bg-yellow-50 dark:bg-gray-800"
      : playingStatus === 5 || playingStatus === 8 || playingStatus === 9
        ? "bg-red-50 dark:bg-red-950"
        : "";

    return (
      <>
        <td
          className={`h-10 min-w-12 border-b border-gray-200 py-1 pl-2 pr-1 text-left md:leading-tight dark:border-gray-800 ${cellClasses} ${bgClasses}`}
        >
          {player ? (
            <>
              <div className="mt-0.5 mb-px sm:mb-0.5 flex items-center justify-start">
                <img
                  className="mr-1.5 h-4 w-4 rounded-full border border-gray-200 ring-0.5 ring-gray-400 dark:border-gray-500 dark:ring-gray-400"
                  src={
                    player.teamId
                      ? `/build/images/sport/teams/logos/512/${player.teamId}.png`
                      : ""
                  }
                  alt={`Team colors`}
                />
                <a
                  className="truncate text-sm font-medium tracking-tight sm:text-md md:hover:underline"
                  data-turbo-frame="modal"
                  href={`/afl/${leagueSeasonId}/players/profile/${player.id}`}
                >
                  {player.abbrName || player.name}
                </a>
                <LineupCaptainIcon player={player} fixtureTeam={fixtureTeam} />
              </div>
              <div className="inline-flex whitespace-nowrap tracking-tight gap-1 dark:font-light items-center justify-center text-xxxs sm:text-xxs leading-none text-gray-900 font-normal dark:text-gray-400">
                <IconPlayingStatus
                  player={player}
                  wrapperStyle={"block h-2.5 w-2.5"}
                />
                <OpponentString player={player} />
              </div>
            </>
          ) : (
            <span className="mr-1 flex h-9 items-center justify-start whitespace-nowrap text-sm leading-tight text-gray-600 dark:text-gray-400">
              - empty -
            </span>
          )}
        </td>

        {statCols.map((col, index) => {
          // Calculate delta for this stat using our utility function
          const delta = calculateStatDelta(gamelog, prevGamelog, col);
          const hasChange = delta !== null;

          const isLast = index === statCols.length - 1;

          // Add special highlighting for cells with changes
          const changeBgClasses = hasChange
            ? "bg-yellow-100 dark:bg-gray-700"
            : bgClasses;

          return (
            <td
              key={`${fixtureTeam.teamSeason.id}-${col}-${index}`}
              className={`whitespace-nowrap sm:font-medium md:min-w-0 border-b border-gray-200 py-1 ${isLast ? "pr-2.5" : "pr-1"} text-right dark:border-gray-800 ${cellClasses} ${changeBgClasses}`}
            >
              <div className="flex flex-col items-end">
                <div className={hasChange ? "font-semibold leading-tight" : ""}>
                  {renderStat(gamelog, col)}
                </div>
                {renderStatDelta(delta)}
              </div>
            </td>
          );
        })}
      </>
    );
  }

  const isWide = useViewport(768);
  const showLeft = isWide || selectedTeam === "team1";
  const showRight = isWide || selectedTeam === "team2";

  return (
    <div className="mb-4 border-t md:mx-0 md:border-t-0 dark:border-gray-700">
      <div className="no-scrollbar relative overflow-scroll overscroll-x-none border-b bg-white text-xs md:mx-0 md:overflow-visible md:rounded-md md:shadow-lg dark:border-gray-800 dark:bg-gray-900 dark:text-gray-200 dark:shadow-none">
        <table className="table-fixe w-full border-separate border-spacing-0 md:table-auto">
          <TableHeader isWide={isWide} statCols={statCols} />
          <tbody>
            {lineupFormation.map((pos, posIndex) => {
              if (pos.starters <= 0) return null;

              const players1 = getPlayersByPosition(
                lineup1,
                pos.abbr,
                "starter",
              );
              const players2 = getPlayersByPosition(
                lineup2,
                pos.abbr,
                "starter",
              );

              return Array.from({ length: pos.starters }).map((_, i) => {
                const player1 = players1[i] || null;
                const player2 = players2[i] || null;

                return (
                  <tr
                    key={`pos-${pos.abbr}-${i}`}
                    className="bg-white dark:bg-inherit"
                  >
                    {/* Player 1 */}
                    {showLeft && (
                      <PlayerRow
                        lineup={lineup1}
                        fixtureTeam={fixtureTeam1}
                        player={player1}
                        statCols={statCols}
                      />
                    )}

                    {/* Position Column */}
                    {isWide && (
                      <td className="content-center border-b bg-gray-100 px-0.5 py-1.5 text-center align-middle text-xxs font-medium tracking-tight text-gray-500 dark:border-gray-800 dark:bg-gray-800">
                        <span>{pos.abbr}</span>
                      </td>
                    )}

                    {/* Player 2 */}
                    {showRight && (
                      <PlayerRow
                        lineup={lineup2}
                        fixtureTeam={fixtureTeam2}
                        player={player2}
                        statCols={statCols}
                      />
                    )}
                  </tr>
                );
              });
            })}

            {lineupFormation.some((pos) => pos.emgs > 0) && (
              <>
                <tr className="bg-white dark:bg-inherit">
                  <td
                    colSpan="99"
                    className="h-8 whitespace-nowrap border-b border-gray-200 bg-gray-100 p-0 text-center font-medium md:border-t-0 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                  >
                    Emergencies
                  </td>
                </tr>

                {lineupFormation.map((pos, posIndex) => {
                  if (pos.emgs <= 0) return null;

                  const players1 = getPlayersByPosition(
                    lineup1,
                    pos.abbr,
                    "emg",
                  );
                  const players2 = getPlayersByPosition(
                    lineup2,
                    pos.abbr,
                    "emg",
                  );

                  return Array.from({ length: pos.emgs }).map((_, i) => {
                    const player1 = players1[i] || null;
                    const player2 = players2[i] || null;

                    return (
                      <tr
                        key={`emg-${pos.abbr}-${i}`}
                        className="bg-white dark:bg-inherit"
                      >
                        {/* Emergency Player 1 */}
                        {showLeft && (
                          <PlayerRow
                            lineup={lineup1}
                            fixtureTeam={fixtureTeam1}
                            player={player1}
                            statCols={statCols}
                          />
                        )}

                        {/* Position Column */}
                        {isWide && (
                          <td className="content-center border-b bg-gray-100 px-0.5 py-1.5 text-center align-middle text-xxs font-medium tracking-tight text-gray-500 dark:border-gray-800 dark:bg-gray-800">
                            <span>{pos.abbr}</span>
                          </td>
                        )}

                        {/* Emergency Player 2 */}
                        {showRight && (
                          <PlayerRow
                            lineup={lineup2}
                            fixtureTeam={fixtureTeam2}
                            player={player2}
                            statCols={statCols}
                          />
                        )}
                      </tr>
                    );
                  });
                })}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Helper function to get sorted players by position
function getPlayersByPosition(lineup, pos, type) {
  return lineup.players
    .filter((player) => player.selectedPos === pos && player.type === type)
    .sort((a, b) => a.listOrder - b.listOrder);
}

function OpponentString({ player }) {
  const [sportFixtures] = useMatchupStore((state) => [state.sportFixtures]);
  const fixture = getFixtureByTeamId(sportFixtures, player.teamId);

  if (!fixture) {
    return "Bye";
  }

  const opponent = getOpponentFromFixture(fixture, player.teamId);

  if (!opponent) {
    return "Bye";
  }

  const team = getTeamFromFixture(fixture, player.teamId);
  const vsString = `${opponent.isHome ? "@" : "v"} ${opponent.team.abbr}`;

  // Game completed
  if (fixture.status === 3) {
    const result =
      team.scoreFor > team.scoreAgainst
        ? "W"
        : team.scoreFor < team.scoreAgainst
          ? "L"
          : "D";

    return (
      <span>
        {result}, {team.scoreFor}-{team.scoreAgainst} {vsString}
      </span>
    );
  }

  // Live game
  if (fixture.status === 1 || fixture.status === 2) {
    if (fixture.qtrComplete) {
      return (
        <span>
          End Q{fixture.qtr} {vsString}
        </span>
      );
    }

    return (
      <span>
        Live Q{Math.max(1, fixture.qtr)} {vsString}
      </span>
    );
  }

  // Upcoming game
  return (
    <span>
      {formatDateInLocalTimezone(fixture.startAt)} {vsString}
    </span>
  );
}

export default PlayerStatsTable;
